import React, { cloneElement, useEffect, useRef, useState } from 'react'
import * as Dialog from "@radix-ui/react-dialog";
import { useTranslation } from 'react-i18next';
import { Check, ChevronRight, X } from "lucide-react";
import { cn } from '@/lib/utils';
import { TextButton } from '@/components/common/text-button';
import { SearchBtnIcon } from '@/components/agent-dashboard/assets';
import { TextInput } from '@/components/common/text-input';
import { TruncatedText } from '@/components/common/truncated-text';
import { Image } from '@/components/common/image';
import { CreateActorButton } from '../custom-digital-human';
import { AllStyleModal } from './all-style-modal';
import { ActorCard } from './actor-card';
import { EditVideoPlayModal } from '../modal-edit-video-play';
import { AllActorModal } from './all-actor-modal';
import { useToast } from '@/hooks/use-toast';
import { agentTemplateHooks, useUserManagementHooks } from '@/manager';
import { LoadingOverlay } from '../common/loading-overlay';
import { StyleData } from '@/services/agent-v2-style-list-local';
import { AgentUserActorData } from '@/manager/manager/agent-dashboard-user-management-manager';
import { AddTemplateActiveIcon } from './assets';
import { LarkImageTableItemsData } from '@/services/agent-v2-feishu-tuwen-table-data';
import { TemplateImageAllModal } from './template-image-all-modal';

export interface TemplateImageAddModalProps {
  actors: Actor[];
  voices: Audio[];
  trigger?: React.ReactElement;
  title?: string;
  open?: boolean;
  loading?: boolean;
  selectedTemplate?: LarkImageTableItemsData | null;
  selectedActor?: Actor | null;
  onSave?: (data: { data: LarkImageTableItemsData | null, actor: Actor | null }) => Promise<void>;
  onUpdate?: (data: { data: LarkImageTableItemsData | null, actor: Actor | null }) => Promise<void>;
  onOpenChange?: (open: boolean) => void;
}

/** 图文-添加模板弹窗（模板+数字人） */
export const TemplateImageAddModal: React.FC<TemplateImageAddModalProps> = ({
  trigger,
  actors,
  voices,
  title,
  open: externalOpen,
  onOpenChange,
  loading: externalLoading,
  selectedTemplate,
  selectedActor,
  onSave,
  onUpdate,
}) => {
  const { t } = useTranslation();
  const { toast } = useToast();

  const [internalOpen, setInternalOpen] = useState(false);
  const titleRef = useRef<HTMLHeadingElement>(null); // 用于聚焦标题的ref

  const {
    loading,
    larkImageTemplates,
    larkImageTemplateSearchFormData,
    changeSearchForm,
    resetAndSearch,
    search,
    addLarkImageTemplates,
  } = agentTemplateHooks.useLarkImageTemplatesData();

  const { addActor } = useUserManagementHooks.useActorAction();

  const [initialSearch, setInitialSearch] = useState(false);
  const [shouldApplySelectedTemplate, setShouldApplySelectedTemplate] = useState(false);
  const [shouldApplySelectedActor, setShouldApplySelectedActor] = useState(false);

  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(null);
  const [selectedActorId, setSelectedActorId] = useState<string | null>(null);

  const [modalOpen, setModalOpen] = useState(false);
  const [selectedVideoIndex, setSelectedVideoIndex] = useState(0);

  // 判断是否使用 trigger 模式（trigger 优先）
  const isTriggerMode = !!trigger;

  // 根据模式决定使用哪个 open 状态
  const currentOpen = isTriggerMode ? internalOpen : externalOpen ?? false;

  // 处理 open 状态变化
  const handleOpenChange = async (open: boolean) => {
    if (isTriggerMode) {
      setInternalOpen(open);
    } else {
      onOpenChange?.(open);
    }
    setSelectedTemplateId(null);
    setSelectedActorId(null);
  };

  const handleInitSearch = async () => {
    try {
      setInitialSearch(true)
      await search(1);
    } catch (error) {
      console.log('handleInitSearch error', error);
    } finally {
      setInitialSearch(false);
    }
  }

  useEffect(() => {
    if (currentOpen) {
      // 等待列表加载完，再允许选中
      handleInitSearch().then(() => {
        if (selectedTemplate) {
          setShouldApplySelectedTemplate(true);
        }
      });
      if (selectedActor) {
        setShouldApplySelectedActor(true);
      }
    } else {
      resetAndSearch(Boolean(selectedTemplate));
      setShouldApplySelectedTemplate(false);
      setShouldApplySelectedActor(false);
    }
  }, [currentOpen]);

  // 处理选中模板的位置调整和选中状态
  useEffect(() => {
    if (!shouldApplySelectedTemplate || !selectedTemplate || larkImageTemplates.length === 0) return;
    const existsInTop6 = larkImageTemplates.slice(0, 6).some(
      s => s.fields.templateId === selectedTemplate.fields.templateId
    );
    if (existsInTop6) {
      // 将该风格挪到第一位
      const index = larkImageTemplates.findIndex(s => s.fields.templateId === selectedTemplate.fields.templateId);
      if (index > 0) {
        const newList = [...larkImageTemplates];
        const item = newList.splice(index, 1)[0];
        newList.unshift(item);
        // 替换整个list
        addLarkImageTemplates(newList);
      }
      setSelectedTemplateId(selectedTemplate.fields.templateId);
    } else {
      // 如果不在前六个中，将该风格放在第一个位置
      addLarkImageTemplates(selectedTemplate);
      setSelectedTemplateId(selectedTemplate.fields.templateId);
    }
    setShouldApplySelectedTemplate(false);
  }, [shouldApplySelectedTemplate, larkImageTemplates, selectedTemplate]);

  useEffect(() => {
    if (!shouldApplySelectedActor || !selectedActor || actors.length === 0) return;
    const existsInTop5 = actors.slice(0, 5).some(
      actor => actor.id === selectedActor.id
    );
    if (existsInTop5) {
      // 将该数字人挪到第一位
      const index = actors.findIndex(actor => actor.id === selectedActor.id);
      if (index > 0) {
        const newList = [...actors];
        const item = newList.splice(index, 1)[0];
        newList.unshift(item);
        // 替换整个list
        addActor(newList as unknown as AgentUserActorData[]);
      }
      setSelectedActorId(selectedActor.id);
    } else {
      // 如果不在前五个中，将该数字人放在第一个位置
      addActor(selectedActor as unknown as AgentUserActorData);
      setSelectedActorId(selectedActor.id);
    }
    setShouldApplySelectedActor(false);
  }, [shouldApplySelectedActor, actors, selectedActor])

  // 处理模板选择
  const handleTemplateSelect = (id: string) => {
    setSelectedTemplateId(prev => {
      if (prev === id) {
        return null; // 取消选择
      } else {
        return id; // 添加选择
      }
    });
  };

  const handleSearch = async () => {
    try {
      await search(1);
    } catch (error) {
      console.warn(error);
    } finally {
      setSelectedTemplateId(null);
    }
  }

  // 处理模板弹窗选择
  const handleTemplateModalSelect = (data: LarkImageTableItemsData) => {
    // 如果选择的不在前六个，将选择的模板数据塞到列表第一个位置，并且选中
    if (!larkImageTemplates.slice(0, 6).some(template => template.fields.templateId === data.fields.templateId)) {
      addLarkImageTemplates(data);
      setSelectedTemplateId(data.fields.templateId);
      return;
    }
    setSelectedTemplateId(data.fields.templateId);
  }

  // 处理数字人弹窗选择
  const handleActorModalSelect = (actor: AgentUserActorData) => {
    // 如果选择的不在前五个，将选择的数字人数据塞到列表第一个位置
    if (!actors.slice(0, 5).some(item => item.id === actor.id)) {
      const newActor = actors.filter(item => item.id !== actor.id);
      addActor([actor, ...newActor as AgentUserActorData[]]);
      setSelectedActorId(actor.id);
      return;
    }
    setSelectedActorId(actor.id);
  }

  // 处理数字人选择
  const handleActorSelect = (actor: Actor) => {
    if (actor.generateStatus !== 2) return;
    setSelectedActorId(prev => {
      if (prev === actor.id) {
        return null; // 取消选择
      } else {
        return actor.id; // 添加选择
      }
    });
  };

  const handleSave = async () => {
    if (!selectedTemplateId || !selectedActorId) {
      toast({
        description: t('agent_dashboard.user_detail.larkImageTemplate.add_modal.save_tips'),
        variant: 'destructive',
      });
      return;
    }
    await onSave?.({
      data: larkImageTemplates.find(template => template.fields.templateId === selectedTemplateId),
      actor: actors.find(actor => actor.id === selectedActorId),
    });
    handleClose();
  }

  // 关闭
  const handleClose = () => {
    handleOpenChange(false);
  };

  // trigger 点击处理
  const handleTriggerClick = () => {
    handleOpenChange(true);
  };

  // 弹窗打开时，强制聚焦到标题
  useEffect(() => {
    if (currentOpen) {
      // 等待DOM更新后执行聚焦（确保元素已挂载）
      setTimeout(() => {
        titleRef.current?.focus();
      }, 0);
    }
  }, [currentOpen]);

  const handleVideoClick = (id: string) => {
    setSelectedVideoIndex(actors.findIndex(actor => actor.id === id));
    setModalOpen(true);
  };

  const dialogContent = (
    <Dialog.Portal>
      <Dialog.Overlay className={cn("fixed inset-0 bg-black/50 z-50")} />
      <Dialog.Content
        className={cn(
          "flex flex-col",
          "fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2",
          "bg-white rounded-[1.25rem] shadow-lg",
          "w-[720px] h-[727px]  flex-shrink-0",
          "z-50 overflow-hidden"
        )}>
        {/* 标题区域 */}
        <div className="flex max-h-[92px] justify-between items-center px-6 pt-6  border-b border-[rgba(233,234,235,1)] pb-5">
          <Dialog.Title
            ref={titleRef}
            tabIndex={-1}
            className='focus:outline-none text-[rgba(24,29,39,1)] text-lg font-semibold flex items-center'
          >
            <div className='size-12 flex justify-center items-center rounded-lg mr-4 border border-[rgba(213,215,218,1)]'>
              <AddTemplateActiveIcon className='size-6' />
            </div>
            <span>{title || t('agent_dashboard.user_detail.template.add_modal.title')}</span>
          </Dialog.Title>
          <Dialog.Close asChild>
            <button
              className="w-10 h-10 flex justify-center items-center hover:bg-gray-100 border border-[rgba(208,213,221,1)] rounded-sm shadow-[0px_1px_2px_rgba(16,24,40,0.05)]"
              tabIndex={-1}
              onClick={handleClose}
            >
              <X className="w-4 h-4 text-[rgba(71,84,103,1)]" />
            </button>
          </Dialog.Close>
        </div>
        {/* 内容 */}
        <div className='flex-1 flex flex-col px-6 pt-5 select-none overflow-y-auto mb-5'>
          {/* 风格区域 */}
          <div>
            <div className='flex items-center justify-between mb-4'>
              <span className='text-[rgba(24,29,39,1)] text-base font-medium'>
                {t('agent_dashboard.user_detail.template.add_modal.choose_style')}
              </span>
              <TemplateImageAllModal
                trigger={(
                  <button className='group flex items-center justify-center gap-1 rounded-lg '>
                    <span className='text-[rgba(102,112,133,1)] text-sm font-medium group-hover:text-[rgba(24,29,39,1)]' transition-all>{t('agent_dashboard.user_detail.template.add_modal.view_all')}</span>
                    <ChevronRight className='size-5 text-[rgba(102,112,133,1)] group-hover:text-[rgba(24,29,39,1)] transition-all' />
                  </button>
                )}
                onSave={handleTemplateModalSelect}
              />
            </div>
            <div className='flex items-center gap-4 mb-5'>
              <TextInput
                type="text"
                placeholder={t('agent_dashboard.user_detail.larkImageTemplate.add_modal.search_placeholder')}
                value={larkImageTemplateSearchFormData.templateId}
                className='h-10'
                onChange={(e) => changeSearchForm('templateId', e.target.value)}
              />
              <TextButton
                label={t('agent_dashboard.user_detail.template.add_modal.search')}
                icon={<SearchBtnIcon className='w-5 h-5' />}
                className={cn(
                  '!w-[106px] h-9 ',
                  'text-white font-semibold text-sm',
                )}
                onClick={handleSearch}
              />
            </div>
            {initialSearch && <Skeleton />}
            {!initialSearch && <div className='grid grid-cols-6 gap-3 relative h-[172px]'>
              {larkImageTemplates?.slice(0, 6)?.map((item, index) => (
                <div key={item.fields.templateId} className="w-full h-full relative">
                  <div
                    className={cn(
                      "w-[102px] h-[136px] group relative rounded-[12px] transition-all cursor-pointer p-0.5 overflow-hidden select-none",
                      selectedTemplateId === item.fields.templateId
                        ? "outline outline-2 outline-i-normal"
                        : "outline outline-transparent"
                    )}
                    onClick={() => handleTemplateSelect(item.fields.templateId)}
                  >
                    <div className='size-full overflow-hidden flex-shrink-0'>
                      <Image src={item.fields.fileurl} className="size-full rounded-[12px] object-cover" />
                    </div>
                    {/* 选中样式 */}
                    <div
                      className={cn(
                        "size-11 bg-black  absolute -bottom-5 -right-5 opacity-0 transition-all duration-150 rounded-tl-[18.4px] rounded-br-[10px] rounded-tr-none rounded-bl-none",
                        selectedTemplateId === item.fields.templateId && "opacity-100"
                      )}
                    >
                    </div>
                    <Check className={cn(
                      "size-4 text-white absolute bottom-0.5 right-1 font-bold opacity-0",
                      selectedTemplateId === item.fields.templateId && "opacity-100"
                    )} />
                  </div>
                  <div className="w-full text-center mt-3">
                    <div className="w-full">
                      <TruncatedText text={item.fields.templateName} className='max-w-[90%] text-base font-medium text-[rgba(16,24,40,1)]' />
                    </div>
                  </div>
                </div>
              ))}
              <LoadingOverlay loading={loading} />
            </div>}
          </div>
          {/* 选择数字人区域 */}
          <div className='mt-5'>
            <div className='flex items-center justify-between mb-4'>
              <span className='text-[rgba(24,29,39,1)] text-base font-medium'>
                {t('agent_dashboard.user_detail.template.add_modal.choose_actor')}
              </span>
              <AllActorModal
                actors={actors}
                onSave={handleActorModalSelect}
                selectedId={selectedActorId}
                voices={voices}
                trigger={(
                  <button className='group flex items-center justify-center gap-1 rounded-lg '>
                    <span className='text-[rgba(102,112,133,1)] text-sm font-medium group-hover:text-[rgba(24,29,39,1)]' transition-all>{t('agent_dashboard.user_detail.template.add_modal.view_all')}</span>
                    <ChevronRight className='size-5 text-[rgba(102,112,133,1)] group-hover:text-[rgba(24,29,39,1)] transition-all' />
                  </button>
                )}
              />
            </div>
            <div className='mt-4 grid grid-cols-6 gap-3'>
              <CreateActorButton className='w-[100px] h-[136px]' textClassName='text-xs mt-1' onCreate={() => { }} />
              {actors.slice(0, 5).map((actor, index) => {
                return (
                  <div className='w-[100px] h-[136px] relative'>
                    <ActorCard
                      key={`${actor.id}_${index}`}
                      actor={actor}
                      name={actor.name}
                      url={actor.video}
                      poster={actor.image}
                      isSelected={selectedActorId === actor.id}
                      status={actor.generateStatus}
                      createdAt={actor.createdAt}
                      onClick={() => handleActorSelect(actor)}
                      onEdit={() => actor.video && handleVideoClick(actor.id)}
                    // onRename={(name) => rename({ id: actor.id, name })}
                    // onRegenerate={() => handleRegenerate(actor)}
                    // onDelete={() => deleteActor(actor.id)}
                    />
                  </div>
                );
              })}
            </div>
          </div>
        </div>
        {modalOpen && (
          <EditVideoPlayModal
            open={modalOpen}
            videos={actors}
            initialIndex={selectedVideoIndex}
            onOpenChange={setModalOpen}
            voices={voices}
          />
        )}
        {/* 操作按钮区域 */}
        <div className='w-full h-[93px] flex justify-end p-6 gap-2 border-t border-[rgba(233,234,235,1)]'>
          <TextButton
            label={t('agent_dashboard.user_detail.template.add_modal.cancel')}
            className='w-[135px] h-11 bg-white border border-[rgba(213,215,218,1)] hover:bg-gray-50 font-semibold text-base !text-[rgba(65,70,81,1)]'
            onClick={handleClose}
          />
          <TextButton
            label={t('agent_dashboard.user_detail.template.add_modal.save')}
            className='w-[135px] h-11'
            onClick={handleSave}
            loading={externalLoading}
            disabled={externalLoading}
          />
        </div>
      </Dialog.Content>
    </Dialog.Portal>
  )

  // trigger 模式
  if (isTriggerMode) {
    return (
      <Dialog.Root open={currentOpen} onOpenChange={handleOpenChange}>
        <Dialog.Trigger asChild>
          {cloneElement(trigger, {
            onClick: (e: React.MouseEvent) => {
              e.stopPropagation();
              e.preventDefault();
              handleTriggerClick();
              // 保留原有的 onClick 事件
              if (trigger.props.onClick) {
                trigger.props.onClick(e);
              }
            },
          })}
        </Dialog.Trigger>
        {dialogContent}
      </Dialog.Root>
    );
  }

  // open 参数模式
  return (
    <Dialog.Root open={currentOpen} onOpenChange={handleOpenChange}>
      {dialogContent}
    </Dialog.Root>
  );
}
const Skeleton: React.FC = () => {
  return (
    <div className="grid grid-cols-6 gap-3 relative">
      {Array.from({ length: 6 }).map((_, index) => (
        <div key={index} className="w-full h-full relative">
          <div
            className="w-[102px] h-[136px] p-0.5 relative rounded-[12px] overflow-hidden select-none animate-pulse"
          >
            {/* 卡片骨架 */}
            <div className="size-full bg-gray-200 rounded-[12px]"></div>
          </div>
          {/* 标题骨架 */}
          <div className="w-full text-center mt-3">
            <div className="w-[80%] h-6 bg-gray-200 rounded mx-auto animate-pulse"></div>
          </div>
        </div>
      ))}
    </div>
  );
};